import { useState } from "react";
import { IoMdArrowBack, IoMdSearch } from "react-icons/io";
import { useNavigate } from "react-router-dom";
import productData from "../Outerpage/productlist";
import categories from "../Outerpage/categoriespage";
import * as api from "../../api/index"
import { jwtDecode } from "jwt-decode";

export default function SmartSearchWithCategories() {
  const token = localStorage.getItem("token") || "";
  let userId = null;

  if (token) {
    try {
      const decoded = jwtDecode(token);
      userId = decoded?.id || decoded?._id || null;
    } catch (err) {
      localStorage.removeItem("token");
    }
  }

  const [searchTerm, setSearchTerm] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [openCategory, setOpenCategory] = useState(null);
  const [conversationLoading, setConversationLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const navigate = useNavigate();

  // --- RESTORED BUSINESS LOGIC ---
  const phoneNumber = "2349026235719";
  const message = encodeURIComponent("Hello,");

  const handleWhatsAppClick = () => {
    window.open(`https://wa.me/${phoneNumber}?text=${message}`, '_blank');
  };

  const handleStartConversation = async () => {
    setConversationLoading(true); 
    setTimeout(async () => {
      if (!userId) return navigate("/auth");
      const dataObject = {
        Name: "Ourasap Service",
        Email: "support@ourasap.com",
        profileImage: "https://ik.imagekit.io/1h5qhnqzz/profiles/F653402A-487E-4336-9FBE-625BE665F4C6_Ge1YoRGCt.jpeg",
        _id: "68d1549e1c0789563e71cd6b"
      };

      try {
        const res = await api.createConversation({
          senderId: userId || "",
          receiverId: "68d1549e1c0789563e71cd6b",
        });

        if (res?.data?.conversation._id) {
          navigate(`/chat/${res.data.conversation._id}`, {
            state: { otherUser: dataObject },
          });
        }
      } catch (err) {
        console.error("Conversation Error:", err);
        alert("Something went wrong. Please try again.");
      } finally {
        setConversationLoading(false);
      }
    }, 2000); 
  };

const handleAiSearch = async (e) => {
  if (e.key === "Enter" && searchTerm.trim() !== "") {
    try {
      setAiLoading(true);
      // 1. Call the AI route
      const res = await api.aiSearch({ prompt: searchTerm, viewerId: userId });
      
      if (res.data.success) {
        // 2. Navigate and pass the AI's hard work to the next page
        navigate("/connect", { 
          state: { 
            itemsShow: res.data.aiInterpreted, // The "fixed" name (washing machine)
            aiResults: res.data.sellers,      // The actual sellers found
            isAiSearch: true,
            lga: res.data.detectedLga          // The city the AI found (e.g., Ikeja)
          } 
        });
      }
    } catch (err) {
      console.error("AI Search Failed:", err);
      navigate("/connect", { state: { itemsShow: searchTerm } });
    } finally {
      setAiLoading(false);
    }
  }
};

  // --- HELPERS ---
  const getCategoryIcon = (name) => {
    const icons = {
      "Gadgets & Tech": "📲",
      "TV & DVD Equipment": "📺",
      "Home & Living": "🛋️",
      "Kitchen Appliances": "🍳",
      "Style & Beauty": "🧥",
      "Electrical & Wiring": "⚡",
      "Services": "🛠️",
      "Vehicles": "🚗"
    };
    return icons[name] || "";
  };

  const allItems = [];
  productData.forEach((block) => {
    block.products.forEach((product) => {
      allItems.push({ type: "product", name: product.name, category: block.category, subcategory: block.subcategory });
      product.models.forEach((model) =>
        allItems.push({ type: "model", name: model, category: block.category, subcategory: block.subcategory, product: product.name })
      );
    });
  });

  categories.forEach((cat) => {
    cat.sub.forEach((sub) => {
      allItems.push({ type: "subcategory", name: sub.name, category: cat.name, subcategory: sub.name });
    });
  });

  const suggestions = allItems
    .filter((item) => item.name.toLowerCase().includes(searchTerm.toLowerCase()))
    .sort((a, b) => {
      const order = { subcategory: 1, product: 2, model: 3 };
      return order[a.type] - order[b.type];
    });

  const handleSelectSuggestion = (s) => {
    const itemToShow = s.product || s.name;
    navigate("/connect", { state: { itemsShow: itemToShow, subCategory: s.subcategory, category: s.category } });
    setShowSuggestions(false);
    setSearchTerm("");
  };

  const handleSelectSubcategory = (catName, subName) => {
    navigate("/connect", { state: { itemsShow: subName, subCategory: subName, category: catName } });
  };

  const scrollSlider = (direction) => {
    const slider = document.getElementById("quickScroll");
    if (slider) {
      const scrollAmount = direction === "left" ? -250 : 250;
      slider.scrollBy({ left: scrollAmount, behavior: "smooth" });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col pb-20 overflow-x-hidden">
      
      {/* 🔝 Sticky Header */}
      <div className="sticky top-0 z-50 bg-white/80 backdrop-blur-md px-4 py-4 flex items-center justify-between border-b border-gray-100">
        <button 
          onClick={() => navigate(-1)} 
          className="group flex items-center text-gray-800 font-black text-sm uppercase tracking-tighter"
        >
          <div className="group-hover:animate-wiggle">
            <IoMdArrowBack size={24} className="mr-1" />
          </div>
          Back
        </button>

        <button
          onClick={handleWhatsAppClick}
          className="bg-teal-600 hover:bg-teal-700 text-white font-black text-[10px] uppercase px-5 py-2 rounded-full shadow-md transition-all active:scale-95"
        >
          Send Errands
        </button>
      </div>

      <div className="max-w-4xl mx-auto w-full p-6 space-y-12">
        
        {/* 🔍 Search Section */}
        <div className="space-y-6">
          <div className="text-center">
            <h1 className="text-4xl font-black text-gray-900 tracking-tighter">Smart Search</h1>
            <p className="text-gray-400 text-[10px] font-black uppercase tracking-widest mt-1">Locate Sellers Instantly</p>
          </div>

          <div className="relative max-w-lg mx-auto w-full">
            <div className="absolute inset-y-0 left-5 flex items-center text-gray-400 pointer-events-none">
              <IoMdSearch size={22} />
            </div>
          <input
  type="text"
  placeholder={aiLoading ? "Agent is thinking..." : "I want to buy a washi machie for 200k"}
  value={searchTerm}
  onKeyDown={handleAiSearch} // 👈 ADD THIS LINE
  onChange={(e) => { setSearchTerm(e.target.value); setShowSuggestions(true); }}
  className={`w-full pl-14 pr-6 py-5 bg-white rounded-[2rem] shadow-xl ... ${aiLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
  disabled={aiLoading}
/>

            {showSuggestions && searchTerm && (
              <div className="absolute top-full left-0 w-full bg-white rounded-[2rem] shadow-2xl mt-3 max-h-80 overflow-y-auto z-40 p-2 no-scrollbar border border-gray-50">
                {suggestions.map((s, i) => (
                  <div key={i} onClick={() => handleSelectSuggestion(s)} className="p-4 hover:bg-teal-50 rounded-2xl cursor-pointer flex items-center justify-between group">
                    <div>
                      <div className="font-black text-gray-900 text-sm">{s.name}</div>
                      <div className="text-gray-400 text-[9px] font-black uppercase tracking-tighter mt-1">{s.subcategory} • {s.category}</div>
                    </div>
                    <span className="text-teal-500 font-black">→</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ⚡ Instant Picks with Fashion and Slider */}
        <div className="space-y-4 relative">
          <div className="flex items-center justify-between px-2">
            <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Instant Picks</h3>
            <div className="flex gap-2">
              <button 
                onClick={() => scrollSlider("left")}
                className="w-8 h-8 rounded-full bg-white border border-gray-100 shadow-sm flex items-center justify-center text-xs hover:bg-teal-50 active:scale-90"
              >◀</button>
              <button 
                onClick={() => scrollSlider("right")}
                className="w-8 h-8 rounded-full bg-white border border-gray-100 shadow-sm flex items-center justify-center text-xs hover:bg-teal-50 active:scale-90"
              >▶</button>
            </div>
          </div>
          
          <div 
            id="quickScroll" 
            className="flex overflow-x-auto gap-4 no-scrollbar px-2 pb-2 scroll-smooth"
          >
            {[
              
             { emoji: "📲", name: "📲 Smartphones & Tablets", cat: "Gadgets & Tech" },
              { emoji: "📺", name: "📺 TV & DVD Equipment", cat: "TV & DVD Equipment" },
              { emoji: "🖥️", name: "🖥️ Laptop & Computers", cat: "Gadgets & Tech" },
              { emoji: "🛋️", name: "🛋️ Furniture", cat: "Home & Living" },
              { emoji: "🍳", name: "🍳 Kitchen Appliances", cat: "Kitchen Appliances" },
             
              { emoji: "🧥", name: "🧥 Men’s Fashion", cat: "Style & Beauty" },
              { emoji: "👠", name: "👠 Women’s Fashion", cat: "Style & Beauty" },
              { emoji: "⚡", name: "⚡ Electrical & Wiring", cat: "Electrical & Wiring" },
            ].map((item, i) => (
              <div
                key={i}
                onClick={() => handleSelectSubcategory(item.cat, item.name)}
                className="flex flex-col items-center justify-center min-w-[115px] h-[115px] bg-white rounded-[2.5rem] shadow-sm hover:shadow-md transition-all cursor-pointer border border-gray-100"
              >
                <span className="text-3xl mb-1">{item.emoji}</span>
                <span className="text-[9px] font-black uppercase tracking-tighter text-gray-800 text-center px-2">{item.name}</span>
              </div>
            ))}
          </div>
        </div>

        {/* 📂 Category Accordion List */}
        <div className="space-y-3">
          <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] px-2">Browse Catalog</h3>
          {categories.map((cat) => (
            <div key={cat.name} className="overflow-hidden">
              <button
                onClick={() => setOpenCategory(openCategory === cat.name ? null : cat.name)}
                className={`w-full flex justify-between items-center p-6 rounded-[1.5rem] transition-all
                  ${openCategory === cat.name ? 'bg-gray-900 text-white shadow-xl' : 'bg-white hover:bg-gray-50 text-gray-900 shadow-sm border border-gray-100'}`}
              >
                <div className="flex items-center gap-4">
                  <span className="text-xl">{getCategoryIcon(cat.name)}</span>
                  <span className="font-black text-sm tracking-tight uppercase">{cat.name}</span>
                </div>
                <span className="text-2xl text-orange-500">{openCategory === cat.name ? "−" : "+"}</span>
              </button>

              {openCategory === cat.name && (
                <div className="p-4 mt-2 grid grid-cols-2 sm:grid-cols-3 gap-3 animate-in fade-in slide-in-from-top-2">
                  {cat.sub.map((sub) => (
                    <div
                      key={sub.name}
                      onClick={() => handleSelectSubcategory(cat.name, sub.name)}
                      className="bg-white border border-gray-100 rounded-2xl p-4 text-center text-[10px] font-black uppercase tracking-tighter text-gray-600 hover:border-teal-400 hover:text-teal-600 transition-all cursor-pointer shadow-sm"
                    >
                      {sub.name}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Support Link */}
        <div className="flex justify-center pt-4">
          <button onClick={() => navigate("/contactUs")} className="text-[10px] font-black text-gray-300 uppercase tracking-widest hover:text-teal-500 transition-colors">
            {conversationLoading ? "Loading..." : "Contact Support"}
          </button>
        </div>
      </div>

      <style dangerouslySetInnerHTML={{ __html: `
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        @keyframes wiggle {
          0%, 100% { transform: rotate(0deg); }
          25% { transform: rotate(-10deg); }
          75% { transform: rotate(10deg); }
        }
        .animate-wiggle { animation: wiggle 0.4s ease-in-out infinite; }
      `}} />
    </div>
  );
}